import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CAROUSEL,
  CAROUSEL_CARD,
  CONTAINER,
  CONTENT_CARD,
  FORM_STEP,
  GROUP,
  MENU,
  PAGE,
  SECTION_COLUMN,
} from "constants/widgetTypes";

import {
  toggleEditMode,
  updateWidgetProperty,
  updateWidgetContent,
  actionGroup,
} from "actions/editor";
import { EditorContext } from "../../index";

import {
  calculateBoundsForElement,
  getParentContainerOfType,
  isWidgetHasHotspotChild,
  getWidgetType,
  isTopParentIsHotspot,
} from "components/Editor/helper/widget";
import {
  useHotspotContainerSelector,
  useAllowCreateLiveElement,
} from "components/Editor/hooks/useEditorSession";
import {
  HotspotActionButton,
  HotspotCreationProperty,
} from "../../components/hotspot";

import _ from "lodash";
import cx from "classnames";
import { Property } from "../../components/properties/";

import WidgetActions from "components/Editor/components/properties/widgetActions";
import StoryboardContext from "components/Storyboard/context";
import { MediaLibraryModal } from "components/MediaLibrary";
import { LiveElementCreationProperty } from "../../components/liveElements";
import { getMediaInfo } from "actions/pageHealth";
import { VariableProperty } from "components/Editor/components/variables";
import { BrandMediaProperty } from "components/Editor/components/brandElements";
import {
  Animation,
  AnimationProperty,
} from "components/Editor/components/animations";

import styles from "./video.module.scss";
import userRoleService from "services/userRoles.service";
import DropShadow from "components/Editor/components/properties/dropShadow";
import { getBoxShadow } from "services/cssHelper.service";


const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const VideoWidgetComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
}) {
  const dispatch = useDispatch();

  const videoRef = React.useRef(null);

  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");
  const content = _.get(widgetProps, "content");

  const widgetStyles = {
    ...getStyles(widgetProps, breakpoint)
  }

  return (
    <>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.video, "widget")}
        style={style}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
      >
        <Animation widget={widgetProps}>
          <VideoView
            url={content ? content.url : ""}
            id={id}
            ref={videoRef}
            widgetStyles={widgetStyles}
          />
        </Animation>
      </div>
    </>
  );
};

const videoWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const VideoWidget = React.memo(VideoWidgetComponent, videoWidgetEquals);

export const VideoView = React.forwardRef(({ url, width, className, widgetStyles }, videoRef) => {
  const style = {
    width: width,
    ...widgetStyles
  };

  useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.load();
    }
  }, [url]);

  return (
    <video
      ref={videoRef}
      name="media"
      className={cx(styles.videoPlayer, className)}
      muted
      style={style}
    >
      <source src={`${IMAGE_CDN_ENDPOINT}/${url}`} />
    </video>
  );
});

const VideoWidgetDeskptopAction = (props) => {
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const entityType = _.get(editorContext, "entityType", null);

  const isHotspotContainer = useHotspotContainerSelector(
    props.editorSession,
    props
  );
  const allowLiveElement = useAllowCreateLiveElement(
    props.editorSession,
    props
  );

  const allowVariable = useSelector((state) =>  
    _.get(state, `${props.editorSession}.present.entityType`)) === 'PAGE';

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${props.editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const fields = [
    { name: 'Video', path: 'content', type: "VIDEO" }
  ];

  const [state, setState] = useState({
    view: null,
    library: false,
    animation: false,
  });

  const storyboardContext = React.useContext(StoryboardContext);
  const id = _.get(props, "uid", null);
  const template = _.get(props, "template.widget") != null;

  const toggleImageLibrary = () => {
    setState({
      library: true,
      animation: false,
    });
  };

  const changeView = () => {
    setState({
      library: false,
      animation: true,
    });
  };

  useEffect(() => {
    setState((state) => {
      return {
        ...state,
        view: null,
      };
    });
  },[id])

  const handleUpdateVideo = (medias) => {
    setState((state) => {
      return {
        ...state,
        library: false,
      };
    });

    const media = Array.isArray(medias) ? medias[0] : medias;
    const naturalWidth = _.get(media, "width") || 600;
    const naturalHeight = _.get(media, "height") || 600;

    var desktopWidth = Math.min(600, naturalWidth);
    var desktopHeight = (desktopWidth * naturalHeight) / naturalWidth;

    var mobileWidth = Math.min(300, naturalWidth);
    var mobileHeight = (mobileWidth * naturalHeight) / naturalWidth;

    actionGroup.start();

    dispatch(updateWidgetContent(props.editorSession, props.uid, media));

    const desktopLayoutProperty = _.get(props, "properties.desktop.layout");
    const mobileLayoutProperty = _.get(props, "properties.mobile.layout");

    dispatch(
      updateWidgetProperty(props.editorSession, props.uid, "desktop", {
        layout: {
          ...desktopLayoutProperty,
          width: `${desktopWidth}px`,
          height: `${desktopHeight}px`,
        },
      })
    );

    dispatch(
      updateWidgetProperty(props.editorSession, props.uid, "mobile", {
        layout: {
          ...mobileLayoutProperty,
          width: `${mobileWidth}px`,
          height: `${mobileHeight}px`,
        },
      })
    );

    actionGroup.end();
  };

  const handleLiveElement = () => {
    setState((state) => {
      return {
        ...state,
        view: "liveElements",
      };
    });
  };

  const handleVariable = () => {
    setState((state) => {
      return {
        ...state,
        view: "variables",
      };
    });
  };

  const handleHotspot = () => {
    setState((state) => {
      return {
        ...state,
        view: "hotspot",
      };
    });
  };

  const handleBrandMedia = () => {
    setState((state) => {
      return {
        ...state,
        view: "brandMedia",
      };
    });
  };

  const closePopup = () => {
    dispatch(toggleEditMode(props.editorSession, props.uid, false));

    setState((state) => {
      return {
        ...state,
        view: null,
      };
    });
  };

  return (
    <>
      <WidgetActions.Action
        className={styles.textButton}
        onClick={toggleImageLibrary}
      >
        Change Video
      </WidgetActions.Action>
      <WidgetActions.Divider />
      <Property.Action
        className={styles.iconButton}
        tooltip="Border"
        onClick={() => setState((state) => {
          return {
            ...state,
            view: "borders",
          };
        })}
      >
        <i className={"cleverstory-corner-radius"} />
      </Property.Action>
      <Property.Action
        className={styles.iconButton}
        tooltip="Animation"
        onClick={() => changeView()}
      >
        <i className={"cleverstory-animations"} />
      </Property.Action>
      {!template && allowLiveElement && (
        <Property.Action
          className={styles.iconButton}
          tooltip="Live Element"
          onClick={handleLiveElement}
        >
          <i className={"cleverstory-live-element"} />
        </Property.Action>
      )}
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={handleBrandMedia}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer && (
        <HotspotActionButton onClick={handleHotspot} widget={props} />
      )}

      {allowVariable && 
        <Property.Action
          className={styles.iconButton}
          tooltip="Make Variable"
          onClick={handleVariable}
        >
          <i className={"cleverstory-variable"} data-track-event="Mixpanel" data-track-props="Make Variable" data-widget-type="Videos"/>
        </Property.Action>
      }

      <WidgetActions.Divider />

      {state.view === "liveElements" && (
        <LiveElementCreationProperty
          elements={[props]}
          onCancel={() => closePopup()}
        />
      )}

      {state.view === "hotspot" && (
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      )}

      { state.view === 'variables' &&
        <VariableProperty widget={props} onCancel={() => closePopup()} fields={fields} onConfirm={()=>{console.log("uploaded variable")}} />
      } 
      {state.view === 'brandMedia' &&
        <BrandMediaProperty widget={props} onCancel={() => closePopup()} mediaType={'video'} />
      }
      {state.view === 'borders' &&
        <BorderPropertyDialog
          id={id}
          editorSession={props.editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      <MediaLibraryModal
        show={state.library}
        storyboard={storyboardContext}
        mediaTypes={["video"]}
        onSelect={handleUpdateVideo}
        onClose={() =>
          setState((state) => {
            return {
              ...state,
              library: false,
            };
          })
        }
        target={
          entityType === "live_element"
            ? {
                id: editorContext.editorSession.id.split("_")[1],
                type: "live_element",
              }
            : null
        }
      />

      {!state.library && state.animation && (
        <AnimationProperty
          ids={[props.uid]}
          editor={props.editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            setState((state) => {
              return {
                ...state,
                animation: false,
              };
            });
          }}
        />
      )}
    </>
  );
};

const BorderPropertyDialog = ({ id, editorSession, widget, ...rest }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const border = _.get(properties, 'border', {});
  const borderWidth = _.get(border, 'width', 0);
  const shadowProperties = _.get(properties, 'shadow', {});
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    const updatedValue = _.omit(value, ['layout']);

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, updatedValue));

    if(breakpoint !== 'mobile') dispatch(updateWidgetProperty(editorSession, id, 'mobile', updatedValue));
  }

  const [borderColor, setBorderColor] = useState(_.get(border, 'color', null));

  const handleBorderColor = (value) => {
    setBorderColor(value);
  };

  useEffect(() => {
    if(_.isEqual(borderColor, _.get(border, `color`))){
      return
    }
    handleBorderColorChange(borderColor);
  }, [borderColor]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  const handleShadowChange = (field, value) => {

    var request = {
      ...properties,
      shadow: {
        ...shadowProperties,
        [field]: value
      }
    }
    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Dialog className={styles.fontDialog}
        width={246}
        target={`#${id}`}
        title={'Border'}
        onCancel={rest.onCancel}
      >
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColor} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.formStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>
        <DropShadow id={id} widget={widget} properties={shadowProperties} handleShadowChange={handleShadowChange} />
      </Property.Dialog>
    </>
  );
};

const VideoWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState("style");

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${props.editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const isHotspotContainer = useHotspotContainerSelector(
    props.editorSession,
    props
  );
  const template = _.get(props, "template.widget") != null;

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action
        className={styles.iconButton}
        tooltip="Border"
        onClick={() => changeView('borders')}
      >
        <i className={"cleverstory-corner-radius"} />
      </Property.Action>
      <Property.Action
        className={styles.iconButton}
        tooltip="Animation"
        onClick={() => changeView("animation")}
      >
        <i className={"cleverstory-animations"} />
      </Property.Action>
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer && (
        <HotspotActionButton
          onClick={() => changeView("hotspot")}
          widget={props}
        />
      )}

      {!editMode && view === "animation" && (
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {view === "hotspot" && (
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      )}
      {view === 'brandMedia' &&
        <BrandMediaProperty widget={props} onCancel={() => closePopup()} mediaType={'video'} />
      }
      {view === 'borders' &&
        <BorderPropertyDialog
          id={id}
          editorSession={props.editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      }
    </>
  );
};

VideoWidget.sizeConfig = async (widget, mediaCache) => {
  if (!_.get(widget, "content.url")) {
    return {
      size: 0,
      type: "video",
    };
  }

  return {
    size:
      (await getMediaInfo(
        `${IMAGE_CDN_ENDPOINT}/${_.get(widget, "content.url", "")}`,
        mediaCache
      )) || 0, // in MB
    type: "video",
  };
};

VideoWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(
    editorSession,
    widget.uid,
    CONTENT_CARD
  );
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ["se", "sw", "ne", "nw"],
    onReparent: (widget, parent) => {
      let hasTopParentIsHotspot = false;
      const parentWidget = getParentContainerOfType(
        editorSession,
        widget,
        CONTENT_CARD
      );

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      if (parentType === CAROUSEL_CARD){
        hasTopParentIsHotspot = isTopParentIsHotspot(editorSession, parent, CAROUSEL);
      }

      const denyHostpotReparent =
        hasHotspotChild &&
        ![PAGE, SECTION_COLUMN, CAROUSEL_CARD, MENU, FORM_STEP, CONTAINER].includes(
          parentType
        );

      if (parentWidget || group || denyHostpotReparent || (hasHotspotChild && hasTopParentIsHotspot)) {
        return false;
      }

      return true;
    },
  };
};

VideoWidget.actions = (editorSession, breakpoint, props) => {
  if (breakpoint === "desktop") {
    return (
      <VideoWidgetDeskptopAction editorSession={editorSession} {...props} />
    );
  }

  if (breakpoint === "mobile") {
    return <VideoWidgetMobileAction editorSession={editorSession} {...props} />;
  }

  return false;
};

VideoWidget.processRequest = (widget) => {
  var content = _.get(widget, "content.id");
  return {
    ...widget,
    content: content,
  };
};

function getStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const deviceProperties = _.get(properties, `properties.${breakpoint}`);

  const topLeft = _.get(deviceProperties, 'border.radius.topLeft', null);
  const topRight = _.get(deviceProperties, 'border.radius.topRight', null);
  const bottomLeft = _.get(deviceProperties, 'border.radius.bottomLeft', null);
  const bottomRight = _.get(deviceProperties, 'border.radius.bottomRight', null);
  const border = _.get(deviceProperties, 'border', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(deviceProperties, 'border.color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(deviceProperties, 'border.width', null) || "0";
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;
  styles['borderStyle'] = "solid";

  if(parseInt(border?.width)) {
    styles['objectFit'] = "fill";
  } else {
    delete styles['objectFit'];
  }

  //DropShadow
  const shadow = _.get(deviceProperties, `shadow`, null);
  const shadowStyle = getBoxShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;
}

export { VideoWidget };
